import { ArrowUpRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import chatMessageService from "../../services/chatMessageService";
import * as S from "../dashboard/_shared";
import "./style.scss";

// Nomes de driver abreviados (ex.: "Agend. Consultas", "Atend. Operadora") — o
// ponto faz parte da abreviação e não deve virar quebra de linha de frase.
const _ABBREV_RX = /\b(?:Agend|Atend|Prom|Ext|Op)\.\s+/g;
const _BOLD_RX = /\*\*(.+?)\*\*/g;

// Quebra `str` em pedaços de frase, inserindo <br/> após ponto final — exceto
// quando o ponto pertence a uma abreviação de driver protegida por _ABBREV_RX.
function _splitSentences(str, kr) {
  const out = [];
  const segs = [];
  let lastIdx = 0;
  _ABBREV_RX.lastIndex = 0;
  let am;
  while ((am = _ABBREV_RX.exec(str)) !== null) {
    if (am.index > lastIdx) segs.push({ prot: false, t: str.slice(lastIdx, am.index) });
    segs.push({ prot: true, t: am[0] });
    lastIdx = _ABBREV_RX.lastIndex;
  }
  if (lastIdx < str.length) segs.push({ prot: false, t: str.slice(lastIdx) });

  segs.forEach((seg) => {
    if (seg.prot) {
      out.push(seg.t);
    } else {
      seg.t.split(/(\.\s+(?=[A-ZÁÀÂÃÉÊÍÓÔÕÚÇ])|\.$)/).forEach((s) => {
        if (!s) return;
        if (/^\.\s*$/.test(s)) {
          out.push(".", <br key={kr.k++} />);
        } else {
          out.push(s);
        }
      });
    }
  });
  return out;
}

// Formata a resposta da Eva: **negrito** em estilo markdown primeiro (pra não
// deixar a proteção de abreviação cortar um marcador ao meio), depois quebra
// de linha por frase dentro de cada segmento (negrito ou não).
function formatEvaText(text) {
  if (!text) return text;
  const out = [];
  const kr = { k: 0 };

  const boldSegs = [];
  let last = 0;
  let m;
  _BOLD_RX.lastIndex = 0;
  while ((m = _BOLD_RX.exec(text)) !== null) {
    if (m.index > last) boldSegs.push({ bold: false, t: text.slice(last, m.index) });
    boldSegs.push({ bold: true, t: m[1] });
    last = _BOLD_RX.lastIndex;
  }
  if (last < text.length) boldSegs.push({ bold: false, t: text.slice(last) });

  boldSegs.forEach((seg) => {
    const pieces = _splitSentences(seg.t, kr);
    out.push(seg.bold ? <strong key={kr.k++}>{pieces}</strong> : pieces);
  });

  return out;
}

export default function EvaChat({
  chatAtivo,
  messages,
  onMessagesUpdate,
  periodName,
  cityFilter,
  onTitleUpdate,
}) {
  const { C } = S;
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const streamingRef = useRef("");
  const timerRef = useRef(null);
  const scrollRef = useRef(null);
  const onMessagesUpdateRef = useRef(onMessagesUpdate);
  useEffect(() => { onMessagesUpdateRef.current = onMessagesUpdate; }, [onMessagesUpdate]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingText]);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const animateWords = (fullText, withUser) => {
    const parts = fullText.split(/(\s+)/);
    let i = 0;
    streamingRef.current = "";

    timerRef.current = setInterval(() => {
      if (i < parts.length) {
        streamingRef.current += parts[i];
        setStreamingText(streamingRef.current);
        i++;
      } else {
        clearInterval(timerRef.current);
        timerRef.current = null;
        const reply = streamingRef.current;
        streamingRef.current = "";
        setStreaming(false);
        setStreamingText("");
        onMessagesUpdateRef.current([...withUser, { role: "assistant", content: reply }]);
      }
    }, 28);
  };

  const send = async () => {
    if (!input.trim() || streaming) return;

    const question = input.trim();
    setInput("");

    const userMsg = { role: "user", content: question };
    const withUser = [...messages, userMsg];
    onMessagesUpdate(withUser);
    S.markEvaPergunta();

    const isFirstUserMessage = messages.length <= 1;

    streamingRef.current = "";
    setStreaming(true);
    setStreamingText("");

    try {
      const fullText = await chatMessageService.postAsk({
        message: question,
        sender: "user",
        fk_id_chat: chatAtivo.id,
        period: periodName,
        city: cityFilter ?? "",
      });

      if (isFirstUserMessage && onTitleUpdate) {
        const title = question.length > 42 ? question.slice(0, 39) + "…" : question;
        onTitleUpdate(chatAtivo.id, title);
      }

      animateWords(fullText, withUser);
    } catch (err) {
      streamingRef.current = "";
      setStreaming(false);
      setStreamingText("");
      onMessagesUpdateRef.current([
        ...withUser,
        { role: "assistant", content: `Desculpe, ocorreu um erro: ${err.message}` },
      ]);
    }
  };

  const sugs = [
    "Por que o atendimento é tão difícil?",
    "O que mais satisfaz os beneficiários?",
    "Como está o NPS deste período?",
    "Você indicaria a Unimed?",
  ];

  const sorted = [...(messages || [])].sort((a, b) => {
    if (a.createdAt != null && b.createdAt != null) return a.createdAt - b.createdAt;
    if (a.id != null && b.id != null) return a.id - b.id;
    return 0;
  });

  return (
    <div className="compEvaChat">
      <div className="messageList">
        {sorted.map((m, i) => (
          <div
            key={i}
            className={`messageBubbleWrapper ${m.role === "user" ? "is-user" : "is-assistant"}`}
          >
            <div className={`messageBubble ${m.role === "user" ? "is-user" : "is-assistant"}`}>
              {m.role === "user" ? m.content : formatEvaText(m.content)}
            </div>
          </div>
        ))}

        {streaming && (
          <div className="messageBubbleWrapper is-assistant">
            <div className="messageBubble is-assistant">
              {streamingText ? formatEvaText(streamingText) : <span className="typingDots"><span /><span /><span /></span>}
            </div>
          </div>
        )}

        <div ref={scrollRef} />
      </div>

      {messages.length <= 2 && messages.length > 0 && !streaming && (
        <div className="suggestions">
          {sugs.map((s, i) => (
            <button key={i} onClick={() => setInput(s)} className="suggestionBtn">
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="inputRow">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Pergunte sobre a experiência dos beneficiários..."
          disabled={messages.length === 0}
          className="chatInput"
        />
        <button
          onClick={send}
          disabled={streaming || !input.trim()}
          className={`sendBtn ${streaming || !input.trim() ? "is-disabled" : "is-active"}`}
        >
          <ArrowUpRight size={16} />
        </button>
      </div>
    </div>
  );
}
